<template>
	<div class="table-saved-views">
		<div class="title-row">
			<slot name="title" />
			<span class="title-separator">/</span>
			<Popover.Root v-model:open="isOpen" :positioning="{ placement: 'bottom-start', gutter: 4 }">
				<Popover.Trigger as-child>
					<button type="button" class="trigger">
						<AppIcon :name="IconName.BookmarkOutline" class="icon" />
						<span class="name">{{ triggerName }}</span>
						<AppIcon :name="IconName.ChevronDown" class="chevron" :class="{ open: isOpen }" />
					</button>
				</Popover.Trigger>

				<Popover.Positioner>
					<Popover.Content class="saved-views-popover">
						<div
							role="button"
							tabindex="0"
							class="item"
							:class="{ active: !activeSavedView && !activePresetView }"
							@click="selectDefaultView"
							@keydown.enter.prevent="selectDefaultView"
						>
							<span class="main">
								<span class="name">{{ t(DEFAULT_VIEW_NAME_KEYS[scope]) }}</span>
							</span>
						</div>
						<div
							v-for="presetView in presetViews"
							:key="presetView.id"
							role="button"
							tabindex="0"
							class="item"
							:class="{ active: presetView.id === activePresetView?.id }"
							@click="selectPresetView(presetView)"
							@keydown.enter.prevent="selectPresetView(presetView)"
						>
							<span class="main">
								<input
									v-if="renamingId === presetView.id"
									v-model.trim="renamingName"
									class="rename-input"
									type="text"
									@click.stop
									@keydown.stop
									@keydown.enter.prevent="commitPresetRename(presetView)"
									@keydown.esc.prevent="renamingId = null"
									@blur="commitPresetRename(presetView)"
								/>
								<span v-else class="name">{{ presetName(presetView.preset) }}</span>
								<span class="summary">{{ summaryText(presetView.view) }}</span>
							</span>
							<span class="buttons">
								<button
									v-if="renamingId === presetView.id"
									type="button"
									class="icon-button"
									:title="t('table-saved-views.save')"
									:aria-label="t('table-saved-views.save')"
									@mousedown.prevent
									@click.stop="commitPresetRename(presetView)"
									@keydown.stop
								>
									<AppIcon :name="IconName.Check" :size="16" />
								</button>
								<button
									v-else
									type="button"
									class="icon-button"
									:title="t('table-saved-views.rename')"
									:aria-label="t('table-saved-views.rename')"
									@click.stop="startPresetRename(presetView)"
									@keydown.stop
								>
									<AppIcon :name="IconName.PencilEdit" :size="16" />
								</button>
							</span>
						</div>

						<div class="separator" />

						<p v-if="tableSavedViewStore.loading" class="empty">{{ t("table-saved-views.loading") }}</p>
						<p v-else-if="hasLoadError" class="error">{{ t("table-saved-views.load-error") }}</p>
						<p v-else-if="savedViews.length === 0" class="empty">{{ t("table-saved-views.empty") }}</p>

						<div v-for="savedView in savedViews" :key="savedView.id">
							<div v-if="deleteCandidateId === savedView.id" class="item confirm-delete">
								<span class="main">
									<span class="name">
										{{ t("table-saved-views.delete-confirm", { name: savedViewName(savedView) }) }}
									</span>
								</span>
								<Button variant="ghost" size="xs" class="delete" @click="confirmDelete(savedView)">
									{{ t("table-saved-views.delete") }}
								</Button>
								<Button variant="ghost" size="xs" class="cancel" @click="deleteCandidateId = null">
									{{ t("table-saved-views.cancel") }}
								</Button>
							</div>
							<div
								v-else
								role="button"
								tabindex="0"
								class="item"
								:class="{ active: savedView.id === activeSavedView?.id }"
								@click="selectSavedView(savedView)"
								@keydown.enter.prevent="selectSavedView(savedView)"
							>
								<span class="main">
									<input
										v-if="renamingId === savedView.id"
										v-model.trim="renamingName"
										class="rename-input"
										type="text"
										@click.stop
										@keydown.stop
										@keydown.enter.prevent="commitRename(savedView)"
										@keydown.esc.prevent="renamingId = null"
										@blur="commitRename(savedView)"
									/>
									<span v-else class="name">{{ savedViewName(savedView) }}</span>
									<span class="summary">{{ summaryText(viewOf(savedView)) }}</span>
								</span>
								<span class="buttons">
									<button
										v-if="renamingId === savedView.id"
										type="button"
										class="icon-button"
										:title="t('table-saved-views.save')"
										:aria-label="t('table-saved-views.save')"
										@mousedown.prevent
										@click.stop="commitRename(savedView)"
										@keydown.stop
									>
										<AppIcon :name="IconName.Check" :size="16" />
									</button>
									<button
										v-else
										type="button"
										class="icon-button"
										:title="t('table-saved-views.rename')"
										:aria-label="t('table-saved-views.rename')"
										@click.stop="startRename(savedView)"
										@keydown.stop
									>
										<AppIcon :name="IconName.PencilEdit" :size="16" />
									</button>
									<button
										type="button"
										class="icon-button"
										:title="t('table-saved-views.delete')"
										:aria-label="t('table-saved-views.delete')"
										@click.stop="deleteCandidateId = savedView.id"
										@keydown.stop
									>
										<AppIcon :name="IconName.Trash" :size="16" />
									</button>
								</span>
							</div>
						</div>

						<div class="separator" />

						<form v-if="isNaming" class="naming" @submit.prevent="submitNewSavedView">
							<input
								ref="nameInputRef"
								v-model.trim="newName"
								type="text"
								:placeholder="t('table-saved-views.name-placeholder')"
								:disabled="isSaving"
							/>
							<Button variant="ghost" size="xs" type="submit" :disabled="isSaving || !newName">
								{{ t("table-saved-views.save") }}
							</Button>
							<Button variant="ghost" size="xs" class="cancel" :disabled="isSaving" @click="cancelNaming">
								{{ t("table-saved-views.cancel") }}
							</Button>
						</form>
						<button v-else type="button" class="item" @click="startNaming">
							<AppIcon :name="IconName.Plus" class="action-icon" />
							<span class="name">{{ t("table-saved-views.save-as-new") }}</span>
						</button>
						<p v-if="error" class="error">{{ error }}</p>
					</Popover.Content>
				</Popover.Positioner>
			</Popover.Root>

			<span v-if="isModified" class="unsaved-badge">{{ t("table-saved-views.unsaved-changes") }}</span>

			<Teleport defer to="#table-saved-view-actions">
				<div class="actions" :class="{ hidden: !isModified }">
					<Button variant="ghost" size="xs" class="reset" @click="discardChanges">
						<AppIcon :name="IconName.RotateCcw" :size="16" class="reset-icon" />
						{{ t("table-saved-views.reset") }}
					</Button>
					<div class="save">
						<Button
							variant="primary"
							size="xs"
							shape="pill"
							class="save-main"
							:class="{ split: hasActiveView }"
							:disabled="isSaving"
							@click="saveMain"
						>
							{{ t("table-saved-views.save") }}
						</Button>
						<Menu v-if="hasActiveView" :gutter="6" placement="bottom-end" @select="onSaveOptionSelect">
							<template #trigger>
								<Button
									variant="primary"
									size="xs"
									shape="pill"
									class="save-options"
									:disabled="isSaving"
									:aria-label="t('table-saved-views.save-options')"
								>
									<AppIcon :name="IconName.ChevronDown" :size="16" />
								</Button>
							</template>
							<MenuItem :value="SaveOption.Update">
								<span class="save-option">
									<AppIcon :name="IconName.Check" :size="16" class="option-icon" />
									<span class="option-text">
										<span class="option-title">
											{{ t("table-saved-views.update-view", { name: triggerName }) }}
										</span>
										<span class="option-description">{{ t("table-saved-views.update-view-description") }}</span>
									</span>
								</span>
							</MenuItem>
							<MenuItem :value="SaveOption.SaveAsNew">
								<span class="save-option">
									<AppIcon :name="IconName.PlusLarge" :size="16" class="option-icon plus" />
									<span class="option-text">
										<span class="option-title">{{ t("table-saved-views.save-as-new-view") }}</span>
										<span class="option-description">
											{{ t("table-saved-views.save-as-new-view-description", { name: triggerName }) }}
										</span>
									</span>
								</span>
							</MenuItem>
						</Menu>
					</div>
				</div>
			</Teleport>
		</div>

		<p v-if="error && !isOpen" class="error">{{ error }}</p>
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { Popover, type MenuSelectionDetails } from "@ark-ui/vue"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import Button from "@/components/ui/Button.vue"
import Menu from "@/components/ui/Menu.vue"
import MenuItem from "@/components/ui/MenuItem.vue"
import { SearchContextPreset, SearchContextScope, type SearchContext } from "@/graphql"
import { UseRootStore } from "@/model/RootStore"
import {
	DuplicateSavedViewNameError,
	type PresetView,
	summarizeTableView,
	useTableSavedViewStore,
} from "@/stores/tableSavedViewStore"
import type { View } from "@/types"

const DEFAULT_VIEW_NAME_KEYS: Record<SearchContextScope, string> = {
	[SearchContextScope.OpportunitiesTable]: "table-saved-views.default-view.opportunities",
	[SearchContextScope.OpportunitiesHuntingTable]: "table-saved-views.default-view.targets",
	[SearchContextScope.TransactionsTable]: "table-saved-views.default-view.transactions",
}

const PRESET_NAME_KEYS: Record<SearchContextPreset, string> = {
	[SearchContextPreset.Multiresidential]: "property.builtCategory.multiresidential",
	[SearchContextPreset.Industrial]: "property.builtCategory.industrial",
}

enum SaveOption {
	Update = "update",
	SaveAsNew = "save-as-new",
}

const props = defineProps<{
	scope: SearchContextScope
	projectId: string | null
}>()

const { t } = useI18n()
const store = UseRootStore()
const tableSavedViewStore = useTableSavedViewStore()

const isOpen = ref(false)
const isNaming = ref(false)
const isSaving = ref(false)
const newName = ref("")
const renamingId = ref<string | null>(null)
const renamingName = ref("")
const deleteCandidateId = ref<string | null>(null)
const error = ref("")
const hasLoadError = ref(false)
const nameInputRef = ref<HTMLInputElement | null>(null)

const savedViews = computed(() => tableSavedViewStore.savedViews(props.scope))
const activeSavedView = computed(() => tableSavedViewStore.activeSavedView(props.scope))
const presetViews = computed(() => tableSavedViewStore.presetViews(props.scope))
const activePresetView = computed(() => tableSavedViewStore.activePresetView(props.scope))
const isModified = computed(() => tableSavedViewStore.isModified(props.scope))
const hasActiveView = computed(() => activeSavedView.value != null || activePresetView.value != null)
const triggerName = computed(() => {
	if (activeSavedView.value) return savedViewName(activeSavedView.value)
	if (activePresetView.value) return presetName(activePresetView.value.preset)
	return isModified.value ? t("table-saved-views.custom-view") : t(DEFAULT_VIEW_NAME_KEYS[props.scope])
})

watch(
	[() => props.scope, () => props.projectId, () => store.isUserLoggedIn],
	([scope, projectId, isLoggedIn]) => {
		if (!isLoggedIn) return
		hasLoadError.value = false
		tableSavedViewStore.fetchSavedViews(scope, projectId).catch((e: unknown) => {
			hasLoadError.value = true
			console.error(e)
		})
	},
	{ immediate: true }
)

watch(isOpen, open => {
	if (open) return
	cancelNaming()
	renamingId.value = null
	deleteCandidateId.value = null
	error.value = ""
})

function summaryText(view: View): string {
	const summary = summarizeTableView(view)
	return [
		t("table-saved-views.summary-filters", { count: summary.filters }, summary.filters),
		sortSummaryText(summary.firstSort, summary.sorts),
		summary.hasCustomColumns ? t("table-saved-views.summary-custom-columns") : null,
	]
		.filter(Boolean)
		.join(" · ")
}

function sortSummaryText(firstSort: NonNullable<View["order"]>[number] | null, sorts: number): string {
	const label = firstSort ? tableSavedViewStore.sortLabel(props.scope, firstSort.field) : null
	if (!firstSort || !label) return t("table-saved-views.summary-sorts", { count: sorts }, sorts)

	const direction = firstSort.direction === "desc" ? "↓" : "↑"
	const sortText = t("table-saved-views.summary-sort", { label, direction })
	return sorts > 1 ? `${sortText} +${sorts - 1}` : sortText
}

function presetName(preset: SearchContextPreset): string {
	return t(PRESET_NAME_KEYS[preset])
}

function savedViewName(savedView: SearchContext): string {
	return savedView.preset && savedView.usesPresetName ? presetName(savedView.preset) : savedView.name
}

function viewOf(savedView: SearchContext): View {
	return tableSavedViewStore.viewOf(props.scope, savedView)
}

async function runAction(action: () => Promise<void>): Promise<void> {
	isSaving.value = true
	error.value = ""
	try {
		await action()
	} catch (e) {
		if (e instanceof DuplicateSavedViewNameError) {
			error.value = t("table-saved-views.duplicate-name")
		} else {
			error.value = e instanceof Error ? e.message : String(e)
		}
	} finally {
		isSaving.value = false
	}
}

function selectDefaultView(): void {
	tableSavedViewStore.applyDefaultView(props.scope)
	isOpen.value = false
}

function selectPresetView(presetView: PresetView): void {
	tableSavedViewStore.applyPresetView(props.scope, presetView)
	isOpen.value = false
}

function selectSavedView(savedView: SearchContext): void {
	if (renamingId.value === savedView.id) return
	tableSavedViewStore.applySavedView(props.scope, savedView)
	isOpen.value = false
}

async function startNaming(): Promise<void> {
	error.value = ""
	isOpen.value = true
	isNaming.value = true
	await nextTick()
	nameInputRef.value?.focus()
}

function cancelNaming(): void {
	isNaming.value = false
	newName.value = ""
}

async function submitNewSavedView(): Promise<void> {
	if (!newName.value) return
	await runAction(async () => {
		await tableSavedViewStore.createSavedView(props.scope, newName.value, props.projectId)
		isOpen.value = false
	})
}

async function saveActiveView(): Promise<void> {
	const activePreset = activePresetView.value
	await runAction(() =>
		activePreset
			? tableSavedViewStore.saveActivePresetView(props.scope, presetName(activePreset.preset))
			: tableSavedViewStore.saveActiveSavedView(props.scope)
	)
}

function discardChanges(): void {
	tableSavedViewStore.discardChanges(props.scope)
}

function saveMain(): void {
	if (hasActiveView.value) {
		saveActiveView()
	} else {
		startNaming()
	}
}

function onSaveOptionSelect({ value }: MenuSelectionDetails): void {
	if (value === SaveOption.Update) saveActiveView()
	if (value === SaveOption.SaveAsNew) startNaming()
}

function startRename(savedView: SearchContext): void {
	renamingId.value = savedView.id
	renamingName.value = savedViewName(savedView)
}

function startPresetRename(presetView: PresetView): void {
	renamingId.value = presetView.id
	renamingName.value = presetName(presetView.preset)
}

async function commitPresetRename(presetView: PresetView): Promise<void> {
	if (renamingId.value !== presetView.id) return
	const name = renamingName.value
	if (!name || name === presetName(presetView.preset)) {
		renamingId.value = null
		return
	}
	await runAction(async () => {
		await tableSavedViewStore.renamePresetView(props.scope, presetView, name)
		renamingId.value = null
	})
}

async function commitRename(savedView: SearchContext): Promise<void> {
	if (renamingId.value !== savedView.id) return
	const name = renamingName.value
	if (!name || name === savedViewName(savedView)) {
		renamingId.value = null
		return
	}
	await runAction(async () => {
		await tableSavedViewStore.renameSavedView(props.scope, savedView.id, name)
		renamingId.value = null
	})
}

async function confirmDelete(savedView: SearchContext): Promise<void> {
	deleteCandidateId.value = null
	await runAction(() => tableSavedViewStore.deleteSavedView(props.scope, savedView.id))
}
</script>

<style scoped>
.table-saved-views {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-xxs);

	.title-row {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: var(--spacing-xs);
	}

	.title-separator {
		margin-left: var(--spacing-xs);
		color: var(--neutral-400);
		font-size: var(--font-size-large);
	}

	.trigger {
		display: inline-flex;
		align-items: center;
		gap: var(--spacing-xs);
		padding: var(--spacing-xxs) var(--spacing-xs);
		border: none;
		border-radius: var(--border-radius-base);
		background: transparent;
		cursor: pointer;
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-large);
		font-weight: var(--font-weight-medium);

		&:hover {
			background-color: var(--neutral-50);
		}

		.icon {
			width: var(--size-icon-md);
			height: var(--size-icon-md);
			color: var(--primary-green);
			transform: translateY(-1px);
		}

		.chevron {
			width: var(--size-icon-md);
			height: var(--size-icon-md);
			color: var(--neutral-600);
			transition: transform 0.2s ease;

			&.open {
				transform: rotate(180deg);
			}
		}
	}

	.unsaved-badge {
		padding: var(--spacing-xxs) var(--spacing-sm);
		border-radius: var(--border-radius-full);
		background-color: var(--neutral-100);
		color: var(--neutral-700);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-medium);
		white-space: nowrap;
	}

	.error {
		margin: 0;
		color: var(--accent-warning);
		font-size: var(--font-size-xs);
	}
}

.actions {
	display: flex;
	align-items: center;
	gap: var(--spacing-sm);

	&.hidden {
		visibility: hidden;
	}

	.reset {
		gap: var(--space-6);
		color: var(--neutral-600);
		font-weight: var(--font-weight-semibold);

		.reset-icon {
			transform: translateY(-1px);
		}
	}

	.save {
		display: flex;

		.save-main,
		.save-options {
			height: calc(var(--size-button-s) - var(--space-2));
			font-weight: var(--font-weight-bold);
		}
	}

	.reset,
	.save-main,
	.save-options {
		&:focus-visible {
			outline: none;
		}
	}

	.save-main.split {
		padding-right: var(--spacing-smd);
		border-top-right-radius: 0;
		border-bottom-right-radius: 0;
	}

	.save-options {
		padding: 0 var(--spacing-smd) 0 var(--spacing-sm);
		border-left-color: color-mix(in srgb, var(--primary-green) 70%, white);
		border-top-left-radius: 0;
		border-bottom-left-radius: 0;
	}
}

.save-option {
	display: flex;
	align-items: flex-start;
	gap: var(--spacing-sm);
	padding: var(--spacing-xs) var(--spacing-smd) var(--spacing-xs) 0;

	.option-icon {
		flex-shrink: 0;
		margin-top: 2px;
		color: var(--primary-green);

		&.plus {
			color: color-mix(in srgb, var(--primary-green) 80%, white);
		}
	}

	.option-text {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
	}

	.option-title {
		color: var(--neutral-900);
		font-weight: var(--font-weight-semibold);
	}

	.option-description {
		color: var(--neutral-600);
		font-size: var(--font-size-xs);
	}
}

.saved-views-popover {
	width: var(--width-saved-views-menu);
	max-width: calc(100vw - 2 * var(--spacing-md));
	display: flex;
	flex-direction: column;
	padding: var(--spacing-xs);
	background-color: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-lg);
	box-shadow: var(--shadow-md);
	z-index: 1000;

	.item {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		width: 100%;
		padding: var(--spacing-sm) var(--spacing-smd);
		border: none;
		border-radius: var(--border-radius-base);
		background: transparent;
		cursor: pointer;
		text-align: left;
		font-family: var(--font-family-primary);

		&:hover {
			background-color: var(--neutral-50);
		}

		&.active {
			background-color: var(--neutral-100);
		}

		.main {
			flex: 1;
			align-self: flex-start;
			min-width: 0;
			display: flex;
			flex-direction: column;
		}

		.name {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			color: var(--neutral-900);
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-medium);
		}

		.summary {
			color: var(--neutral-600);
			font-size: var(--font-size-xs);
		}

		.buttons {
			flex-shrink: 0;
			display: flex;
			gap: var(--spacing-sm);
		}

		.rename-input {
			width: 100%;
			margin-bottom: var(--spacing-xxs);
			padding: var(--spacing-xxs) var(--spacing-sm);
			border: var(--border-width-thin) solid var(--neutral-400);
			border-radius: var(--border-radius-md);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			outline: none;
		}

		.icon-button {
			flex-shrink: 0;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: var(--size-button-s);
			height: var(--size-button-s);
			padding: 0;
			border: none;
			border-radius: var(--border-radius-sm);
			background: transparent;
			cursor: pointer;
			color: var(--neutral-600);

			&:hover {
				background-color: var(--neutral-200);
				color: var(--neutral-900);
			}
		}

		&.confirm-delete {
			cursor: default;
			background-color: var(--neutral-50);

			.name {
				font-weight: var(--font-weight-regular);
			}

			.delete {
				color: var(--accent-warning);
			}

			.cancel {
				color: var(--neutral-600);
			}
		}

		.action-icon {
			width: var(--size-icon-sm);
			height: var(--size-icon-sm);
			color: var(--neutral-600);
		}
	}

	.naming {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		padding: var(--spacing-xs) var(--spacing-sm);

		input {
			flex: 1;
			min-width: 0;
			height: var(--size-button-s);
			padding: 0 var(--spacing-sm);
			border: var(--border-width-thin) solid var(--neutral-400);
			border-radius: var(--border-radius-full);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-small);
			outline: none;

			&:focus {
				border-color: var(--neutral-900);
			}
		}

		.cancel {
			color: var(--neutral-600);
		}
	}

	.separator {
		height: var(--border-width-thin);
		margin: var(--spacing-xxs) var(--spacing-sm);
		background-color: var(--neutral-200);
	}

	.error {
		margin: 0;
		padding: 0 var(--spacing-sm) var(--spacing-xs);
		color: var(--accent-warning);
		font-size: var(--font-size-xs);
	}

	.empty {
		margin: 0;
		padding: var(--spacing-sm);
		color: var(--neutral-600);
		font-size: var(--font-size-xs);
	}
}
</style>
